import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  useTheme,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AccountsandFinanceurl, SDLSUrl } from "../../../config";
import DynamicTableForJson from "../../../data/DynamicTable/dynamicTableForJson";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../../theme";

const base_url = SDLSUrl.uri;

const STAGES = ["Field Officer", "Branch", "Region", "Company"];

// Single source of truth for the 4 approval-stage variants, mirroring
// AsssetLoanManagement/Approvals/ApprovalTable.jsx — each stage only
// differs by which of these flags is set.
const LEVEL_CONFIG = {
  fieldOfficer: {
    label: "Field Officer",
    stepIndex: 0,
    parameters: { isCompany: 0, isRegion: 0, isBranch: 0, isFieldOfficer: 1 },
  },
  branch: {
    label: "Branch",
    stepIndex: 1,
    parameters: { isCompany: 0, isRegion: 0, isBranch: 1, isFieldOfficer: 0 },
  },
  region: {
    label: "Region",
    stepIndex: 2,
    parameters: { isCompany: 0, isRegion: 1, isBranch: 0, isFieldOfficer: 0 },
  },
  company: {
    label: "Company",
    stepIndex: 3,
    parameters: { isCompany: 1, isRegion: 0, isBranch: 0, isFieldOfficer: 0 },
  },
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const columns = [
  { field: "Id", headerName: "ID", flex: 0.6 },
  { field: "CustomerId", headerName: "Customer ID", flex: 1 },
  { field: "CustomerPhone", headerName: "Customer Phone", flex: 1 },
  { field: "FirstName", headerName: "First Name", flex: 1 },
  { field: "OtherNames", headerName: "Other Names", flex: 1.2 },
  { field: "ApplicationDate", headerName: "Booked Date", flex: 1.2 },
  { field: "LoanAmount", headerName: "Loan Amount", flex: 1 },
  { field: "InstallmentAmount", headerName: "Installment Amount", flex: 1 },
  { field: "ProductName", headerName: "Product", flex: 1 },
  {
    field: "Imei1",
    headerName: "Device",
    flex: 1.4,
    renderCell: (params) => {
      const label = [params.row.ModelName, params.row.Imei1]
        .filter(Boolean)
        .join(" — ");
      return label || (params.row.DeviceId ? `#${params.row.DeviceId}` : "—");
    },
  },
  { field: "Name", headerName: "Branch", flex: 1 },
];

const DeviceLoanApprovalTable = ({ level }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const config = LEVEL_CONFIG[level];

  const [accountBalance, setAccountBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(
    config.parameters.isCompany === 1,
  );

  useEffect(() => {
    if (config.parameters.isCompany !== 1) return;
    (async () => {
      try {
        const response = await fetchJsonDynamic(
          "orgAccountBalance",
          null,
          null,
          AccountsandFinanceurl.uri,
        );
        if (response?.results) {
          const parsed = JSON.parse(response.results);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setAccountBalance(parsed[0].OrgAccountBalance);
          }
        }
      } catch (err) {
        console.error("Failed to fetch org account balance:", err);
      } finally {
        setLoadingBalance(false);
      }
    })();
  }, [config.parameters.isCompany]);

  const formattedBalance = (value) => {
    if (value === null || value === undefined) return "—";
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const actions = {
    entity: localStorage.getItem("SelectedRight"),
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: false,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: false,
      permission: "CreatePermission",
    },
    DeviceApproveReject: {
      key: "DEVICEAPPROVEREJECT",
      button_name: "Approve/Reject loan",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4, 5],
    },
    dataObject: {
      objectName: "allDeviceLoanApplications",
      inputObjectName: "input",
      parameters: config.parameters,
      ignoreFields: [
        "LoanDisbursed",
        "Installments",
        "IsLoanApproved",
        "IsBranchApproved",
        "FoFirstName",
        "FoLastName",
        "Metadata",
      ],
    },
    showStatus: true,
  };

  return (
    <Box m="20px">
      <Stepper activeStep={config.stepIndex} sx={{ mb: 3 }}>
        {STAGES.map((stage) => (
          <Step key={stage}>
            <StepLabel>{stage}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {config.parameters.isCompany === 1 && (
        <Tooltip
          title="This is the organisation's portal account balance. Always verify that this balance is sufficient before disbursing loans — disbursing without adequate funds will result in failed or blank disbursements."
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 320,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                backgroundColor:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
                color: "#fff",
                p: 1.5,
              },
            },
            arrow: {
              sx: {
                color:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
              },
            },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: colors.primary[400],
              minWidth: 280,
              cursor: "default",
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: 1,
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[300],
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Org Account Balance
                </Typography>
                <InfoOutlinedIcon
                  sx={{ fontSize: 13, color: colors.grey[300] }}
                />
              </Box>
              {loadingBalance ? (
                <CircularProgress size={20} sx={{ mt: 0.5 }} />
              ) : (
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: colors.greenAccent[400] }}
                >
                  KES {formattedBalance(accountBalance)}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.4,
                  color: colors.grey[400],
                  fontStyle: "italic",
                  lineHeight: 1.3,
                }}
              >
                Ensure balance is adequate before disbursing loans.
              </Typography>
            </Box>
          </Paper>
        </Tooltip>
      )}

      <DynamicTableForJson
        title={`${config.label} Device Loan Appraisal`}
        subtitle="Review and approve or reject device-backed loan applications"
        base_url={base_url}
        columns={columns}
        actions={actions}
      />
    </Box>
  );
};

export default DeviceLoanApprovalTable;
